import { Box, Tooltip, useTheme } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useTranslation } from 'react-i18next';
import { toIntlLocale } from '../../language/intlLocale';
import { tokens } from '../../global/theme/tokens';

/**
 * Labels a record an AI agent created (createdByAi) or changed afterwards
 * (aiModifiedAt), both set by the backend (TracksAiOrigin) for everything
 * the MCP tools and the AI chat write. Renders nothing for records a person
 * made and the AI never touched. `compact` shows only the icon, for table
 * rows and cards; the tooltip always carries the full text.
 *
 * Prefer ExoAiOriginAnchor where the record has an avatar, icon or status
 * chip: the tag then floats over its corner and never depends on the title.
 */
export const ExoAiOriginChip = ({
  record,
  compact = false,
  className = '',
  sx = {},
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t, i18n } = useTranslation();

  if (!record?.createdByAi && !record?.aiModifiedAt) return null;

  const modifiedAt = record.aiModifiedAt
    ? new Date(record.aiModifiedAt).toLocaleString(toIntlLocale(i18n.language), {
        dateStyle: 'medium',
        timeStyle: 'short',
      })
    : null;
  const label = record.createdByAi ? t('AI-generated') : t('AI-edited');
  const tooltip = [
    record.createdByAi ? t('This record was created by the AI assistant.') : null,
    modifiedAt
      ? t('Last changed by the AI assistant on {{date}}.', { date: modifiedAt })
      : null,
  ]
    .filter(Boolean)
    .join(' ');
  const color = colors.info;

  return (
    <Tooltip title={tooltip} arrow>
      <Box
        component="span"
        role="img"
        aria-label={label}
        className={`inline-flex items-center gap-1 rounded-md align-middle shrink-0 ${
          compact ? 'p-0.5' : 'px-1.5 py-0.5'
        } ${className}`}
        sx={{
          backgroundColor: `${color}20`,
          border: `1px solid ${color}40`,
          color,
          fontSize: '11px',
          fontWeight: 600,
          lineHeight: 1.2,
          whiteSpace: 'nowrap',
          cursor: 'default',
          ...sx,
        }}
      >
        <AutoAwesomeIcon sx={{ fontSize: 13 }} />
        {!compact && label}
      </Box>
    </Tooltip>
  );
};

/**
 * Floats the compact tag over the top-left corner of its child (an avatar,
 * a type icon, a status chip, a checkbox), so the tag sits at the same spot
 * in every row and card, takes no space and never moves with the title.
 * Without an AI origin the child renders as it is. `offset` is how far the
 * tag hangs over the corner, in px; small anchors want a smaller one.
 */
export const ExoAiOriginAnchor = ({
  record,
  children,
  offset = 6,
  className = '',
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  return (
    <Box
      component="span"
      className={`relative inline-flex shrink-0 ${className}`}
      data-testid="ai-origin-anchor"
    >
      {children}
      <ExoAiOriginChip
        record={record}
        compact
        className="absolute"
        sx={{
          top: -offset,
          left: -offset,
          zIndex: 1,
          // the tag lies over the anchor, so its tint needs an opaque
          // backing instead of letting the avatar shine through
          backgroundColor: colors.card,
          backgroundImage: `linear-gradient(${colors.info}20, ${colors.info}20)`,
        }}
      />
    </Box>
  );
};

export default ExoAiOriginChip;
